import React, { useState } from 'react';
import { Field, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { StatusCodes } from 'http-status-codes';
import { useLocation } from 'react-router-dom';
import { RadioGroupField } from '../v3/RadioGroup/RadioGroup';
import { FormikInputField } from '../v3/InputField/FormikInput';
import { SharedDirectorRadioGroup } from '../v3/SharedDirectorRadioGroup/SharedDirectorRadioGroup';

import { FormikSelectField } from '../v3/SelectField/FormikSelect';
import { Checkbox } from '@vodacomsa/ns-react-components';
import { NewDateSelector } from '../NewDateSelector/NewDateSelector';
import styles from './FormBuilder.module.css';
import FormikCustomSelect from '../v3/FormikCustomSelect/FormikCustomSelect';
import { handleCheckboxChange } from './util';
import {
  postDirectorDetails,
  deleteDirectorDetails
} from '../../api/vodaLend';
import { ConfirmModal } from '../PopupModal/PopupModal';
import { AccordionStyle, DirectorDetails } from '../../constants';
import { useModal } from '../modalController/useModal';
import {
  Footer,
  ErrorModalSection,
  BankStatementUpload
} from '..';
import {
  ArrowDown,
  CloseIcon,
  DeleteIcon
} from '../../assets';
import { useShowToast } from '../../recoil';
import { Accordion } from '../Accordion/Accordion';
import Support from '../v3/Support/Support';
import Tooltip from '../v3/ToolTip/Tooltip';
import HighlightClickableText from '../HighlightClickableText/HighlightClickableText';
import { useLoader } from '../Loader/useLoader';
import BusinessFoundNotification from '../BusinessFoundNotification/BusinessFoundNotification';
import { useTealium } from '../../tealium/useTealium';

export const FieldTypes = {
  INPUT: 'input',
  SELECT: 'select',
  CUSTOM_SELECT: 'custom_select',
  CHECKBOX: 'checkbox',
  DATESELECTOR: 'DateSelector',
  PAIRED_FIELDS: 'paired_fields',
  CATEGORY_WITH_HEADING: 'fieldHeading',
  DYNAMIC_BUILDING_FIELDS: 'dynamic_building_fields',
  DYNAMIC_ID_FIELDS: 'dynamic_id_fields',
  DYNAMIC_DIRECTOR_FIELDS: 'dynamic_director_fields',
  CHECKBOX_WITH_INPUT: 'checkbox_with_input',
  RADIOGROUP: 'radio_group',
  LABEL: 'Label',
  CURRENCYINPUT: 'currency_amount_input',
  INPUT_BLUR: 'input_blur',
  SHARED_DIRECTOR_RADIO_GROUP: 'shared_director_radio_group'
};

export const FormBuilder = ({
  fields,
  title,
  subtitle,
  isMobile,
  values,
  FormField,
  errors,
  personalAddress,
  isVatNumberPreFetched,
  submitState,
  isDetailsCaptured,
  isBusinessVerified,
  isSoleTrader,
  backClick,
  submissionStates,
  nextClick,
  touched,
  radioGroupOptions,
  handleRadioGroupChange,
  radioGroupDefaultValue,
  requireFooter = true,
  _journeyType,
  fetchedTotalDirectors,
  shouldShowSubmit,
  isNextAllowed = true,
  businessVerificationResult,
  customBlurHandlers = {},
  shouldShowFileUpload,
  setIsDocumentUploaded,
  isVatOptional,
  sharedDirectorsValue,
  businessList,
  submitDirector,
  showConfirmButton = false,
  showCancelButton = false,
  sharedBusinessIdDirector,
  leftButtonTitle,
  selectedBusiness,
  isNewBusiness,
  pageName
}) => {

  const [tooltipVisible, setTooltipVisible] = useState(null);
  const [isOpenSaveCurrentDirectorConfirmationPopup, setIsOpenSaveCurrentDirectorConfirmationPopup] = useState(false);
  const [isOpenNextConfirmationPopup, setIsOpenNextConfirmationPopup] = useState(false);
  const [openLoader, closeLoader] = useLoader();
  const handleTooltipToggle = (fieldName) => {
    setTooltipVisible(tooltipVisible === fieldName ? null : fieldName);
  };

  const getDefaultActiveAccordion = () => {
    if (!values || !values?.directors) {
      return '';
    };

    const isVerifiedDirector = values?.directors.some(d => d.isIdVerified);
    return isVerifiedDirector ? '' : 'directorDetails.0';
  };

  const { setFieldValue, handleBlur, setTouched, initialValues, validateForm } = useFormikContext();
  const [openModal, closeModal] = useModal();
  const [isAccordionSaved, setIsAccordionSaved] = useState(!getDefaultActiveAccordion());
  const [isfetchDirectorDetailFull, setIsfetchDirectorDetailFull] = useState(false);
  const [isDirectorDetailDeleted, setIsDirectorDetailDeleted] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState(getDefaultActiveAccordion());
  const [addedDirectors, setAddedDirectors] = useState([]);
  const [isOpenDirectorDelete, setOpenDirectorDelete] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState();
  const [totalExistingDirector, setTotalExistingDirector] = useState(fetchedTotalDirectors);
  const showToast = useShowToast();
  const { track, events } = useTealium();
  const location = useLocation();
  const validateAllDirectorSaved = (directorDetails) => {
    const isAllDirectorSaved = directorDetails?.some(director =>
      !director?.directorId
      || !director?.title
      || !director?.firstName
      || !director?.msisdn
      || !director?.email
      || !director?.positionInCompany
      || !director?.surName
    );
    return isAllDirectorSaved;
  };

  const postVerify = () => {
    const verifyRegNumber = sessionStorage.getItem('regNumber');
    const verifyBusinessName = sessionStorage.getItem('businessName');
    const verifyVatNumber = sessionStorage.getItem('vatNumber');
    const isNewBusiness = (verifyBusinessName && (verifyRegNumber || verifyVatNumber)) ? true : false;
    return isNewBusiness;
  };

  const addNewDirectorDiv = (directorDetails, setFieldValue) => {
    const isAllDirectorSaved = validateAllDirectorSaved(directorDetails);
    track(events.businessDirectors.addDirector());
    if (!isAllDirectorSaved) {
      isBusinessVerified && setActiveAccordion(`directorDetails.${directorDetails?.length}`);
      setAddedDirectors([...addedDirectors, { ...DirectorDetails }]);
      setFieldValue('directors', [
        ...directorDetails,
        { ...DirectorDetails }
      ]);
    }
  };

  const clearDirectorItem = (index) => {
    const directorDetails = values.directors;
    directorDetails[index] = DirectorDetails;

    setFieldValue('directors', [
      ...directorDetails
    ]);

    setTouched((prevTouched) => {
      const newTouched = { ...prevTouched };
      if (newTouched.directors && Array.isArray(newTouched.directors)) {
        newTouched.directors[index] = {};
      }
      return newTouched;
    });
  };

  const clearFilledMissingFields = (index, item) => {
    const missingFields = item?.isDataMissing || [];

    if (!Array.isArray(missingFields) || missingFields.length === 0) { return; }

    missingFields.forEach((field) => {
      if (values.directors?.[index]?.[field]) {
        setFieldValue(`directors[${index}].${field}`, '');
      }
    });

    setTouched((prevTouched) => {
      const newTouched = { ...prevTouched };
      newTouched.directors = Array.isArray(newTouched.directors) ? [...newTouched.directors] : [];
      newTouched.directors[index] = { ...(newTouched.directors[index] || {}) };

      missingFields.forEach((field) => {
        newTouched.directors[index][field] = true;
      });

      return newTouched;
    });

    setTimeout(() => {
      if (typeof validateForm === 'function') { validateForm(); }
    }, 0);
  };

  const onDirectorSave = async () => {
    const updatedDirectors = await submitDirector(values);
    setAddedDirectors(updatedDirectors.directors);
    await setFieldValue('directors', [
      ...updatedDirectors.directors
    ]);
    showToast('Directors added successfully!');
    track(events.businessDirectors.saveDirector());
    setIsAccordionSaved(true);
    setActiveAccordion('');
  };

  const idSaveDirectorDisable = (index) => {
    const hasError = errors?.directors && errors?.directors[index] ? true : false;
    return hasError;
  };

  const cancelIt = async (index, item) => {
    if (item?.isDataMissing === undefined || item?.isDataMissing?.length === 0) {
      clearDirectorItem(index);
    } else {
      clearFilledMissingFields(index, item);
    }

  };

  const showDirectorFooterActions = (index) => {
    const director = values?.directors[index];
    if (!director) {return false;}

    if (Array.isArray(director.isDataMissing) && director.isDataMissing.length === 0) {
      return false;
    }
    if (Array.isArray(director.isDataMissing) && director.isDataMissing.length > 0) {
      return true;
    }

    if (!director.directorId) {
      return true;
    }

    if (director.isDeleted) {
      return true;
    }

    if (director.isPartial && director.wasOriginallyPartial && director.isDataMissing?.length > 0) {
      return true;
    }

    return false;
  };

  const removeDirectorDiv = (id, directorDetails, setFieldValue) => {
    if (id > -1 && directorDetails?.length > 0) {
      const newArray = directorDetails.filter((_d, index) => index !== id);
      setFieldValue('directors', newArray);
      setActiveAccordion(''); // Clear active accordion to re-enable "Add another Director" button
    }
  };

  const openDeletePopup = (index, values, fieldValue) => {
    setDeleteInfo({ index, values, fieldValue });
    setTouched((prevTouched) => {
      const newTouched = { ...prevTouched };
      if (newTouched.directors && Array.isArray(newTouched.directors)) {
        newTouched.directors[index] = {};
      }
      return newTouched;
    });
    setOpenDirectorDelete(true);
    setIsAccordionSaved(true);
    track(events.businessDirectors.deleteDirectorPopup());
  };

  const removeSharedDirector = async (id, directorDetails, setFieldValue) => {
    if (id > -1 && directorDetails?.length > 0) {
      const newArray = directorDetails.filter((_d, index) => index !== id);
      await setFieldValue('directors', [
        ...newArray
      ]);
      setActiveAccordion(''); // Clear active accordion to re-enable "Add another Director" button
      showToast('Director deleted');
    }
  };

  const deleteDirector = async () => {
    track(events.businessDirectors.deleteDirector());
    const directorId = deleteInfo?.values && deleteInfo?.values[deleteInfo?.index].directorId;
    const sharedBusinessId = sharedBusinessIdDirector;

    if (!directorId) {
      removeDirectorDiv(deleteInfo?.index, deleteInfo?.values, deleteInfo?.fieldValue);
      setOpenDirectorDelete(false);
      showToast('Director deleted');
      return;
    }

    if (sharedBusinessId && directorId) {
      await removeSharedDirector(deleteInfo?.index, deleteInfo?.values, deleteInfo?.fieldValue);
      return;
    }

    openLoader();
    const deleteDirector = await deleteDirectorDetails({ directorId });
    closeLoader();
    if (deleteDirector.status === StatusCodes.NO_CONTENT) {
      removeDirectorDiv(deleteInfo?.index, deleteInfo?.values, deleteInfo?.fieldValue);
      setOpenDirectorDelete(false);
      setTotalExistingDirector(totalExistingDirector - 1);
      setIsDirectorDetailDeleted(true);
      showToast('Director deleted');
    } else if (deleteDirector?.data?.error) {
      openModal({
        title: 'Error',
        children: <ErrorModalSection
          traceId={deleteDirector?.data?.error?.traceId}
          error={deleteDirector?.data?.error?.message}
          onCloseErrorModal={closeErrorModal}
        />,
        type: 'error',
        onCloseModal: closeErrorModal
      });
      return;
    }
  };

  const renderDynamicIdFields = (idTypeField) => {
    if (!values?.idType) {
      return null;
    }
    const selectedIdFields = idTypeField[values?.idType];
    if (!selectedIdFields) {
      return null;
    }
    return selectedIdFields.map((field, index) => (
      <React.Fragment key={index}>
        {renderField(field)}
      </React.Fragment>
    ));
  };

  const isEmpty = val =>
    val === undefined || val === null || (typeof val === 'string' && val.trim() === '');

  const renderDirectorField = (field, namePrefix, directorIndex) => {
    const director = values?.directors?.[directorIndex];
    if (!director) { return null; }

    const fieldName = field?.props?.name;

    const touchedField = touched?.directors?.[directorIndex]?.[fieldName];
    const hasError = touchedField && errors?.directors?.[directorIndex]?.[fieldName];
    const fieldClassName = !isMobile ? getDirectorErrorClassName(hasError) : getErrorClassName(hasError);

    const isNewDirector = !director?.directorId && !director?.isPartialDirector;

    const isFieldMissing = director?.isDataMissing?.includes(fieldName);

    let shouldDisableField = false;

    if (!isNewDirector) {
      const fieldHasValue = !isEmpty(director?.[fieldName]);
      if (fieldHasValue && (!isFieldMissing && !director?.wasOriginallyPartial)) {
        shouldDisableField = true;
      }

      if (['idType', 'idNumber'].includes(fieldName)) {
        const fieldValue = director?.[fieldName];
        if (fieldName === 'idType') {
          if (fieldValue === 'id' && director?.idNumber !== '' && director?.isIdVerified) {
            shouldDisableField = true;
          } else if (fieldValue !== 'id' && director?.idNumber !== '') {
            shouldDisableField = true;
          }
        }
      }
      if (fieldHasValue && director?.isSFDirectors && director?.wasOriginallyPartial) {
        if(director?.isDataMissing?.includes(fieldName)) {
          shouldDisableField = false;
        } else {
          shouldDisableField = true;
        }
      }

      else if (fieldHasValue && isFieldMissing && !director?.canDelete) {
        shouldDisableField = false;
      }
    } else {
      shouldDisableField = false;
    }

    // Highest priority: allow editing for missing fields
    if (Array.isArray(director?.isDataMissing) && director.isDataMissing.includes(fieldName)) {
      shouldDisableField = false;
    } else {
      shouldDisableField = shouldDisableField || field.props.disabled;
    }

    const disabledField = {
      ...field,
      props: {
        ...field.props,
        disabled: shouldDisableField
      }
    };

    switch (field.type) {
      case FieldTypes.INPUT:
        return renderDirectorInput(disabledField, fieldClassName, namePrefix, directorIndex);
      case FieldTypes.SELECT:
        return renderDirectorSelect(disabledField, fieldClassName, namePrefix);
      case FieldTypes.CUSTOM_SELECT:
        return renderDirectorCustomSelect(disabledField, fieldClassName, namePrefix);
      default:
        return null;
    }
  };

  const renderDynamicDirectorFields = (directorFields, directorIndex) => {
    const namePrefix = (`directors.${directorIndex}`);
    if (!values?.directors[directorIndex]?.isIdVerified) {
      return null;
    }
    const selectedIdFields = directorFields[values?.directors[directorIndex]?.isIdVerified];
    if (!selectedIdFields) {
      return null;
    }
    return selectedIdFields.map((field, index) => (
      <React.Fragment key={index}>
        {renderDirectorField(field, namePrefix, directorIndex)}
      </React.Fragment>
    ));
  };

  const renderDynamicBuildingFields = (buildingTypeFields) => {
    if (!values?.buildingType) {
      return null;
    }
    const selectedTypeFields = buildingTypeFields[values?.buildingType];
    if (!selectedTypeFields) {
      return null;
    }

    return selectedTypeFields.map((field, index) => (
      <React.Fragment key={index}>
        {renderField(field)}
      </React.Fragment>
    ));
  };

  const shouldDisableField = (fieldName) => {
    if (!values.isPersonalAddress) { return false; }

    const addressFields = [
      'unitNumber', 'street', 'suburb', 'postalCode',
      'province', 'country', 'buildingType', 'building',
      'flatUnitFloor', 'address', 'estateName',
      'buildingOfficeParkName', 'unitSuiteNumber', 'flatUnitNumber', 'flatStreet', 'flatBuilding', 'townBuilding',
      'townUnitNumber', 'townStreet', 'officeBuilding', 'officeUnitNumber', 'officeStreet', 'city', 'houseUnitNumber', 'houseStreet'
    ];

    return addressFields.includes(fieldName);
  };

  const getErrorClassName = (hasError, fieldType = '') => {
    if (hasError) {
      return styles.errorContainer;
    } else if (fieldType !== 'paired') {
      return styles.fieldContainer;
    } else {
      return '';
    }
  };

  const shouldDisabled = (field) => {

    let fieldList = ['flatBuilding', 'townBuilding', 'officeBuilding'];
    if (values.isPersonalAddress) {
      if (values?.name) {
        fieldList.push('name');
      }
      if (values?.regNumber) {
        fieldList.push('regNumber');
      }
      if (values?.vatNumber && isVatNumberPreFetched) {
        fieldList.push('vatNumber');
      }
      return fieldList.includes(field.name);
    }

    let shouldDisabled;
    switch (field?.name) {
      case 'idType':
      case 'name':
      case 'regNumber':
      case 'idNumber':
      case 'passportNumber':
        if (isDetailsCaptured) { shouldDisabled = true; };
        break;
      case 'vatNumber':
        if (localStorage.getItem('businessId')) {
          shouldDisabled = true;
        } else if (isVatOptional) {
          shouldDisabled = false;
        } else {
          shouldDisabled = true;
        }
        break;
      case 'country':
        shouldDisabled = true;
        break;
      default:
        shouldDisabled = false;
    }
    return shouldDisabled;
  };

  const closeErrorModal = () => {
    closeModal();
  };

  const handleIdNumberChange = async (idType, idNumber, index) => {

    const hasError = errors?.directors?.[index]?.idNumber;

    if (hasError) {
      return;
    }

    const postBody = { idType, idNumber };

    openLoader();
    const directorVerificationResponse = await postDirectorDetails(postBody);
    const { status, data } = directorVerificationResponse;

    closeLoader();

    if (status === StatusCodes.OK) {

      const { message, data: directorData } = data;

      if (message === 'No Director Found') {

        const updatedDirectors = values.directors.map((v, idx) => ({ ...v, isIdVerified: true, isDirectorFound: index === idx ? false : true }));
        setFieldValue('directors', updatedDirectors);

      } else if (message === 'Director Found') {
        let closeAccordion = false;
        const updatedDirectors = values.directors.map((d, idx) => {
          if (idx === index) {
            const isDataMissing = directorData.isDataMissing ?? [];
            closeAccordion = (isDataMissing.length === 0 ? true : false);
            return {
              ...d,
              ...directorData,
              idNumber: directorData?.idNumber || d?.idNumber || directorData?.maskedIdNumber,
              idType: directorData?.idType || d?.idType,
              isIdVerified: true,
              isDirectorFound: true,
              canDelete: (isDataMissing.length === 0),
              canExpand: (isDataMissing.length === 0)
            };
          }
          return d;
        });
        setIsfetchDirectorDetailFull(true);
        await setFieldValue('directors', updatedDirectors);
        if (closeAccordion) {
          setActiveAccordion('');
          setIsAccordionSaved(true);
        } else {
          setActiveAccordion('');
          setTimeout(() => setActiveAccordion(`directorDetails.${index}`), 0);
        }

      }

    } else if (data.error) {

      openModal({
        title: 'Error',
        children: <ErrorModalSection
          traceId={data?.error?.traceId}
          error={data?.error}
          onCloseErrorModal={closeErrorModal}
        />,
        type: 'error',
        onCloseModal: closeErrorModal
      });

    }
  };

  const shouldDisableDirectorField = (fieldName, previousDirectors, index) => {

    if (values?.directors[index]?.isDataMissing?.includes(fieldName)) {
      return false;
    }

    if (values?.directors[index].isPartialDirector && !['idType', 'idNumber'].includes(fieldName)) {
      return false;
    }

    if (values?.directors[index][fieldName] === '') {
      return false;
    }

    if ((values?.directors[index].isDirectorFound === undefined || values?.directors[index].isDirectorFound) && !values?.directors[index].directorId) {
      return false;
    }

    if (!values?.directors[index].isDirectorFound && !['idType', 'idNumber'].includes(fieldName)) {
      return false;
    }

    if (values?.directors[index].directorId && values?.directors[index].isDeleted) {
      return false;
    }

    return true;
  };

  const renderDirectorInput = (field, fieldClassName, namePrefix, directorIndex) => {
    const pref = namePrefix ? `${namePrefix}.` : '';
    const index = +namePrefix?.split('.')[1];
    // const isPartial = values.directors ? values.directors[directorIndex]?.isPartial : false;

    if (
      field.props.name === 'idNumber' &&
  values?.directors[directorIndex]?.isPartial &&
  !values?.directors[directorIndex]?.isIdVerified
    ) {

      return <div className={field.props?.hint ? styles.btmMargin : ''}>
        <div style={FormField}>
          {field.label && (
            <div className={styles.fieldLabel}>
              {field.label}
            </div>
          )}
          <Field
            {...field.props}
            component={FormikInputField}
            onBlur={(e) => { handleBlur(e); }}
            name={`${pref}${'maskedIdNumber'}`}
            className={fieldClassName}
            disabled={true}
          />
          <Field
            type="hidden"
            name={`${field?.props?.name}`}
          />
        </div>
      </div>;
    }
    return (
      <div className={field.props?.hint ? styles.btmMargin : ''}>
        <div style={FormField}>
          {field.label && (
            <div className={styles.fieldLabel}>
              {field.label}
            </div>
          )}
          <Field
            {...field.props}
            component={FormikInputField}
            onBlur={(e) => {
              handleBlur(e);
              if (field?.props?.name === 'idNumber' && values?.directors[index].idNumber !== '') {
                handleIdNumberChange(values?.directors[index].idType, values?.directors[index].idNumber, index);
              }
            }}
            name={`${pref}${field?.props?.name}`}
            className={fieldClassName}
            disabled={
              field.props.disabled ||
              shouldDisableDirectorField(field?.props?.name, totalExistingDirector - 1, index)}
          />
        </div>
      </div>
    );
  };

  const renderInput = (field, fieldClassName) => {
    return (
      <div className={field.props?.hint ? styles.btmMargin : ''}>
        <div style={FormField}>
          {field.label && (
            <div className={styles.fieldLabel}>
              {field.label}
            </div>
          )}

          <Field component={FormikInputField} {...field.props} className={fieldClassName} disabled={field.props.disabled ? true : shouldDisabled(field.props)} />

        </div>
      </div>
    );
  };

  const renderBusinessVerificationInput = (field, fieldClassName, customBlurHandlers) => {
    return (
      <div className={field.props?.hint ? styles.btmMargin : ''}>
        <div style={FormField}>
          {field.label && (
            <div className={styles.fieldLabel}>
              {field.label}
            </div>
          )}
          {
            businessVerificationResult && businessVerificationResult?.isMatched &&
            <BusinessFoundNotification />
          }
          <Field
            {...field.props}
            component={FormikInputField}
            onBlur={async (e) => {
              handleBlur(e);
              if (typeof customBlurHandlers === 'function') {
                await customBlurHandlers(values, errors);
              }
            }}

            name={field.props.name}
            className={fieldClassName}
            disabled={disableBusinessVerificationInput()}
          />
        </div>
      </div>
    );
  };

  const disableBusinessVerificationInput = () => {
    if (businessVerificationResult?.businessName && businessVerificationResult?.isMatched !== null && (businessVerificationResult?.regNumber || businessVerificationResult?.vatNumber)) {
      return true;
    } else {
      return false;
    }

  };

  const renderCurrencyAmountInput = (field, fieldClassName) => {
    const hasError = Object.keys(touched).includes(field?.props?.name) && errors[field?.props?.name];

    return (
      <div className={field.props?.hint && !hasError ? styles.btmMargin : styles.errorBtmMargin}>
        <div style={FormField}>
          {field.label && (
            <div className={styles.fieldLabel}>
              {field.label}
            </div>
          )}
          <div className={styles.loanInputContainer} >

            {values[field?.props?.name] ?
              <span className={styles.currency} >R{' '}</span>
              :
              <span className={styles.errorCurrency} />
            }
            <div className={styles.inputFieldContainer}>
              <Field component={FormikInputField}
                value={(values[field?.props?.name] ?? '').toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                onChange={(e) => {
                  const raw = e.target.value.replace(/\D/g, '');
                  setFieldValue(field?.props?.name, raw);
                }}
                {...field.props} className={fieldClassName} disabled={field.props.disabled ? true : shouldDisabled(field.props)} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRadioGroup = (field) => {
    return (
      <div>
        <Field {...field.props} defaultValue={radioGroupDefaultValue} handleRadioGroupChange={handleRadioGroupChange} options={radioGroupOptions} component={RadioGroupField} />
      </div>
    );
  };

  const businessRenderRadioGroup = (field, fieldClassName) => {
    const selectedBusinessId = localStorage.getItem('businessId');
    const filteredBusinessList = businessList.filter(biz => biz.value !== selectedBusinessId);
    const props = {
      placeholder: 'Select the business with the shared directors',
      options: [...filteredBusinessList]
    };
    return (
      <>
        <div>
          <Field {...field.props} handleRadioGroupChange={handleRadioGroupChange} component={SharedDirectorRadioGroup} />
        </div>
        <div>
          {
            sharedDirectorsValue === 'Yes' &&
            <Field
              {...props}
              component={FormikCustomSelect}
              className={fieldClassName}
              name='sharedBusinessDirectors'
            />
          }
        </div>
      </>
    );
  };

  const renderDirectorSelect = (field, fieldClassName, namePrefix) => {
    const pref = namePrefix ? `${namePrefix}.` : '';
    const index = +namePrefix?.split('.')[1];

    return (
      <div style={FormField}>
        {field.label && (
          <div className={styles.fieldLabel}>
            {field.label}
          </div>
        )}
        <Field
          {...field.props}
          component={FormikSelectField}
          name={`${pref}${field?.props?.name}`}
          className={fieldClassName}
          disabled={
            field.props.disabled || shouldDisableDirectorField(field?.props?.name, totalExistingDirector - 1, index)}
          onOptionSelect={(selectedValue) => {
            const fieldName = field?.props?.name;
            setFieldValue(fieldName, selectedValue?.value);
            track(events.formBuilder.fieldInteraction(location.pathname, fieldName, selectedValue?.label));
          }}
        />
      </div>
    );
  };

  const renderDirectorCustomSelect = (field, fieldClassName, namePrefix) => {
    const pref = namePrefix ? `${namePrefix}.` : '';
    const index = +namePrefix?.split('.')[1];

    const customFormDetails = {
      fieldName: field?.props?.name,
      errors: errors?.directors ? errors?.directors[index] : null,
      touched: touched && touched?.directors ? touched?.directors[index] : null
    };

    return (
      <div style={FormField}>
        {field.label && (
          <div className={styles.fieldLabel}>
            {field.label}
          </div>
        )}
        <Field
          {...field.props}
          component={FormikCustomSelect}
          name={`${pref}${field?.props?.name}`}
          className={fieldClassName}
          customFormDetails={customFormDetails}
          disabled={
            field.props.disabled || shouldDisableDirectorField(field?.props?.name, totalExistingDirector - 1, index)}
        />
      </div>
    );
  };

  const renderSelect = (field, fieldClassName) => {
    return (
      <div style={FormField}>
        {field.label && (
          <div className={styles.fieldLabel}>
            {field.label}
          </div>
        )}
        <Field component={FormikSelectField} {...field.props} className={fieldClassName}
          disabled={field.props.disabled ? true : shouldDisabled(field.props)}
          onOptionSelect={(selectedValue) => {
            const fieldName = field?.props?.name;
            setFieldValue(fieldName, selectedValue?.value);
            track(events.formBuilder.fieldInteraction(location.pathname, fieldName, selectedValue?.label));
          }}
        />
      </div>
    );
  };

  const renderCustomSelect = (field, fieldClassName) => {
    return (
      <div>
        {field.label && <div className={styles.fieldLabel}>{field.label}</div>}
        <Field
          component={FormikCustomSelect}
          className={fieldClassName}
          disabled={shouldDisableField(field.props.name)}
          {...field.props}
        />
      </div>
    );
  };

  const renderLabel = (field) => {
    return (
      <>
        <div className={styles.label}>
          {field.label}
        </div>
      </>
    );
  };

  const renderCheckbox = (field) => {
    const hasError = (Object.keys(errors).length === 1 && errors[field?.props?.name]);

    return (
      <div className={styles.formFieldRadioContainer}>
        <Field name={field.props.name}>
          {({ form }) => (
            <Checkbox
              style={hasError ? { 'border-color': 'red' } : {}}
              {...field.props}
              type="checkbox"
              id="label-checkbox"
              checked={values[field.props.name]}
              onChange={(e) => { handleCheckboxChange(e, form, field.props.name, personalAddress, location.pathname, track, events); }}
            />
          )}
        </Field>
        {field.props?.checkboxlabel && field.props?.link && (
          <div className={styles.checkboxLabel} onClick={(e) => e.preventDefault()} >
            <HighlightClickableText text={field.props?.checkboxlabel} highlightText={field.props?.link} url={field.props?.linkurl} />
          </div>
        )}
      </div>
    );
  };

  const renderHelpText = (field) => {
    return (
      <div className={styles.categoryHeading} style={{ display: 'flex', alignItems: 'center' }}>
        <span>{field?.props?.label}</span>
        {field?.props?.isHelp && <div style={{ position: 'relative', marginLeft: '8px' }}>

          <Support
            width={20}
            height={20}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              track(events.formBuilder.fieldInteraction(location.pathname, 'catagoryInfoText', tooltipVisible === field?.props?.label ? 'hide' : 'display'));
              handleTooltipToggle(field.props.label);
            }}
          />
          <Tooltip isVisible={tooltipVisible === field?.props?.label} heading={field?.props?.toolTipHeading}
            subHeading={field?.props?.toolTipSubHeading} />

        </div>}
      </div>
    );
  };

  const renderDateSelector = (field, hasError) => {
    return (
      <div className={styles.formFieldDateSelector}>
        <Field
          component={NewDateSelector}
          {...field.props}
          error={hasError}
        />
      </div>
    );
  };

  const renderPairFields = (field) => {
    return (
      <div>
        <div className={styles.fieldRow}>
          <div className={styles.halfWidth} >
            {renderField({
              ...field.fields[0],
              props: {
                ...field.fields[0].props,
                disabled: shouldDisableField(field.fields[0].props.name)
              }
            }, 'paired')}
          </div>
          {field.fields[1] &&
            <div className={styles.halfWidth}>
              {renderField({
                ...field.fields[1],
                props: {
                  ...field.fields[1].props,
                  disabled: shouldDisableField(field.fields[1].props.name)
                }
              }, 'paired')}
            </div>}
        </div>
      </div>
    );
  };

  const renderCheckboxwithInput = (field) => {
    const hasError = Object.keys(touched).includes(field?.conditionalInput?.name) && errors[field?.conditionalInput?.name];
    const fieldClassName = getErrorClassName(hasError, '');
    return (
      <Field name={field?.checkbox?.name}>
        {({ field: formikField, form }) => (
          <>
            <div className={styles.formFieldRadioContainer}>
              <Checkbox
                {...field?.checkbox}
                {...formikField}
                disabled={field?.checkbox?.disabled}
                type="checkbox"
                id="input-checkbox"
                checked={formikField.value}
                onChange={(e) => {
                  track(events.formBuilder.fieldInteraction(location.pathname, formikField.name, !formikField.value));
                  form.handleChange({
                    target: {
                      name: formikField.name,
                      value: e.target.checked
                    }
                  });
                  form.handleChange({
                    target: {
                      name: field?.conditionalInput?.name,
                      value: field?.conditionalInput?.name ? '' : field?.conditionalInput?.name
                    }
                  });
                }}
              />
            </div>

            {formikField.value && (
              <div style={FormField}>
                <Field
                  component={FormikInputField}
                  name={field?.conditionalInput?.name}
                  label={field?.conditionalInput?.label}
                  placeholder={field?.conditionalInput?.placeholder}
                  className={fieldClassName}
                  hideUnderline={true}
                  disabled={field?.conditionalInput?.disabled}
                />
              </div>
            )}
          </>
        )}
      </Field>
    );
  };

  const hasUnsavedOrIncompleteDirectors = () => {
    return values?.directors?.some((d, idx) => {
      if (d.isDeleted) {return false;}

      const initial = initialValues.directors[idx];
      const sanitize = (obj = {}) => {
        const {
          firstName, idNumber, idType, directorId, email, msisdn, surName, title
        } = obj;
        return {
          firstName, idNumber, idType, directorId, email, msisdn, surName, title
        };
      };
      const dirty = JSON.stringify(sanitize(d)) !== JSON.stringify(sanitize(initial));
      const incomplete = Array.isArray(d.isDataMissing) && d.isDataMissing.length > 0;

      return dirty || incomplete;
    });
  };

  const handleNextClick = () => {
    if ((!isfetchDirectorDetailFull && !isDirectorDetailDeleted) && hasUnsavedOrIncompleteDirectors()) {
      setIsOpenNextConfirmationPopup(true);
    } else {
      nextClick();
    }
  };

  const confirmNextClick = () => {
    setIsOpenNextConfirmationPopup(false);
    nextClick();
  };

  const cancelNextClick = () => {
    setIsOpenNextConfirmationPopup(false);
  };

  const CheckBusinessSelected = (list) => {
    return list?.businesses?.some(option => option.value === selectedBusiness?.toString() && !(!option?.isSameBusiness || option?.isSameProduct));
  };

  const rightCallback = () => {
    const isBusinessSelected = CheckBusinessSelected(businessList);
    if (!submitState) {
      return Object.values(errors)?.length ? null : handleNextClick;
    } else if (submitState === submissionStates.canSubmit) {
      return (pageName === 'businessList' && !isBusinessSelected && isNewBusiness === false) ? null : handleNextClick;
    } else {
      return null;
    }
  };

  const getRightButtonLabel = () => {

    if (values?.directors) {
      return 'Next';
    }

    if (shouldShowSubmit) {
      return 'Submit';
    }
    else if (postVerify()) {
      return 'Next';
    }
    else if (isDetailsCaptured) {
      return 'Confirm and continue';
    }
    else if (showConfirmButton) {
      return 'Confirm';
    }
    else {
      return 'Next';
    }
  };

  const getLeftButtonLabel = () => {

    if (leftButtonTitle || leftButtonTitle === '') {
      return leftButtonTitle;
    }

    if (showCancelButton) {
      return 'Cancel';
    }

    return 'Back';
  };

  const getDirectorErrorClassName = (hasError) => {
    if (hasError) {
      return styles.errorDirectorContainer;
    } else {
      return styles.directorPageContainer;
    }
  };

  const renderField = (field, fieldType) => {

    const hasError = Object.keys(touched).includes(field?.props?.name) && errors[field?.props?.name];
    const fieldClassName = getErrorClassName(hasError, fieldType);

    switch (field?.type) {
      case FieldTypes.INPUT:
        return renderInput(field, fieldClassName);

      case FieldTypes.INPUT_BLUR:
        return renderBusinessVerificationInput(field, fieldClassName, customBlurHandlers);

      case FieldTypes.RADIOGROUP:
        return renderRadioGroup(field, fieldClassName);

      case FieldTypes.SHARED_DIRECTOR_RADIO_GROUP:
        return businessRenderRadioGroup(field, fieldClassName);

      case FieldTypes.CURRENCYINPUT:
        return renderCurrencyAmountInput(field, fieldClassName, hasError);

      case FieldTypes.SELECT:
        return renderSelect(field, fieldClassName);

      case FieldTypes.CUSTOM_SELECT:
        return renderCustomSelect(field, fieldClassName);

      case FieldTypes.LABEL:
        return renderLabel(field);

      case FieldTypes.CHECKBOX:
        return renderCheckbox(field);

      case FieldTypes.CATEGORY_WITH_HEADING:
        return renderHelpText(field);

      case FieldTypes.DATESELECTOR:
        return renderDateSelector(field, hasError);

      case FieldTypes.PAIRED_FIELDS:
        return renderPairFields(field);

      case FieldTypes.CHECKBOX_WITH_INPUT:
        return renderCheckboxwithInput(field);

      default:
        return null;
    }
  };

  const renderDirectorFields = (namePrefix) => {
    const directorIndex = +namePrefix?.split('.')[1];

    return fields?.map((field, index) => {
      if (field.type === FieldTypes.DYNAMIC_DIRECTOR_FIELDS) {
        return (
          <React.Fragment key={index}>{renderDynamicDirectorFields(field.fields, directorIndex)}</React.Fragment>
        );
      }
      return <React.Fragment key={index}>{renderDirectorField(field, namePrefix, directorIndex)}</React.Fragment>;
    });
  };

  const renderFormFields = () => {
    return fields?.map((field, index) => {
      if (field?.props?.name === 'buildingType') {
        return (
          <React.Fragment key={index}>
            {renderField(field)}
            {fields
              ?.filter((f) => f.type === FieldTypes.DYNAMIC_BUILDING_FIELDS)
              ?.map((f, idx) => (
                <React.Fragment key={idx}>
                  {renderDynamicBuildingFields(f.fields)}
                </React.Fragment>
              ))}
          </React.Fragment>
        );
      }

      if (field?.type === FieldTypes.DYNAMIC_ID_FIELDS) {
        return (
          <React.Fragment key={index}>{renderDynamicIdFields(field.fields)}</React.Fragment>
        );
      }

      return <React.Fragment key={index}>{renderField(field)}</React.Fragment>;
    });
  };

  const handleAccordionClick = (index) => {
    if (activeAccordion === `directorDetails.${index}`) {
      setActiveAccordion('');
      //setIsAccordionSaved(true);
    } else {
      setActiveAccordion(`directorDetails.${index}`);
      setIsAccordionSaved(false);
    }
  };

  const handleAccordionKeyDown = (index) => {
    setActiveAccordion(
      activeAccordion === `directorDetails.${index}` ? '' : `directorDetails.${index}`
    );
  };

  const hasUnsavedOrPartialDirector = values?.directors?.some((d) => {
    const isUnsaved = !d?.directorId;
    const isStillPartial = d?.wasOriginallyPartial && Array.isArray(d?.isDataMissing) && d.isDataMissing.length > 0;
    return isUnsaved || isStillPartial;
  });

  const getDirectorAccordionTitle = (director, index) => {
    // Masked display for partial director (from BE or manually detected)
    if (
      director.isPartial && director.wasOriginallyPartial && director.maskedIdNumber) {
      return `${index + 1}. ${director.maskedIdNumber}`;
    }
    // Normal case
    if (director.firstName || director.surName) {
      return `${index + 1}. ${director?.title || ''} ${director?.firstName || ''} ${director?.surName || ''}`;
    }
    // Default: Add director
    return `${index + 1}. Add director`;
  };

  const cancelDirectorDeletion = () => {
    setOpenDirectorDelete(false);
    track(events.businessDirectors.cancelDeleteDirector());
  };

  const cancelAddNewDirector = () => {
    setIsOpenSaveCurrentDirectorConfirmationPopup(false);
    track(events.businessDirectors.cancelAddNewDirector());
  };

  const isAddDirectorDisabled = () => {
    if (hasUnsavedOrPartialDirector) {
      return true;
    }
    if (!isAccordionSaved && idSaveDirectorDisable()) {
      return true; }
    return false;
  };

  const handleAddDirectorClick = () => {
    if (!isAddDirectorDisabled()) {
      addNewDirectorDiv(values.directors, setFieldValue);
    }
  };

  const canExpandDirector = (isBusinessVerified, item) => {
    if (!item) { return false; }

    if(!item?.directorId) {
      return true;
    }

    if(item?.wasSFVerified) {
      return false;
    }

    if (item?.isSFDirectors && !item?.wasOriginallyPartial && isBusinessVerified) {
      return true;
    }

    if (item?.isSFDirectors && !item?.wasOriginallyPartial) {
      return false;
    }
    if (item?.isSFDirectors && item?.wasOriginallyPartial) {
      return true;
    }
    if (!item?.isSFDirectors && item?.isAdditionalDirectors) {
      return true;
    }

    if (!isBusinessVerified) {
      return true;
    }

    return false;
  };

  const canDeleteDirector = (isBusinessVerified, item) => {
    if (!item) { return false; }

    if(!item?.directorId) {
      return true;
    }

    if(item?.wasSFVerified) {
      return false;
    }

    if (item.isAdditionalDirectors) {
      return true;
    }

    if (item?.isSFDirectors && item?.wasOriginallyPartial) {
      return false;
    }

    if (item?.isSFDirectors && !item?.wasOriginallyPartial && isBusinessVerified) {
      return true;
    }
    const isPartial =
      item.isPartial || item.isPartialDirector || item.isPartialDirectorsData;

    if (item.isSFDirectors && !isPartial && !item.wasOriginallyPartial) {
      return false;
    }

    if (isBusinessVerified && !isPartial) {
      return false;
    }

    if (isPartial && !item.isSFDirectors) {
      return true;
    }

    if (!item.directorId || item.isAdditionalDirectors) {
      return true;
    }

    return false;
  };

  return (
    <div className={styles.container}>
      <div className={isMobile ? styles.mobileTitleContainer : styles.titleContainer}>
        <div className={isMobile ? styles.mobileTitleHeading : styles.titleHeading}>
          {title}
        </div>
        {subtitle && (
          <div className={styles.titleSubHeading}>
            {subtitle}
          </div>
        )}
      </div>
      {
        values?.directors ?
          <div className={styles.formWrapper}>
            <div className={styles.childAccordionContainer}>
              {values?.directors?.map((item, index) => {
                // const namePrefix=(`directors[${index}]`);
                const namePrefix = (`directors.${index}`);
                const directorIndex = +namePrefix?.split('.')[1];
                return (<Accordion
                  key={index}
                  isDirector={true}
                  isOpen={activeAccordion === `directorDetails.${index}`}
                  headerChildren={
                    <>
                      <div className={styles.accordionHeaderLeft}>
                        <div style={AccordionStyle.accordionTitle}>
                          {getDirectorAccordionTitle(item, index)}
                        </div>
                      </div>

                      <div className={styles.accordionImgContainer}>
                        {canDeleteDirector(isBusinessVerified, item, index) && (
                          <img
                            style={AccordionStyle.deleteImg}
                            src={DeleteIcon}
                            alt="Delete Icon"
                            onClick={() => openDeletePopup(index, values.directors, setFieldValue)}
                            role="presentation"
                          />
                        )}

                        {canExpandDirector(isBusinessVerified, item, index) && (
                          <img
                            style={{
                              ...AccordionStyle.ArrowImg,
                              rotate: activeAccordion === `directorDetails.${index}` ? '180deg' : ''
                            }}
                            src={ArrowDown}
                            alt="DropdownOpen"
                            role="presentation"
                            onClick={() => handleAccordionClick(index)}
                            onKeyDown={() => handleAccordionKeyDown(index)}
                          />
                        )}

                      </div>
                    </>
                  }
                  containerStyle={{ height: 'auto' }}
                >
                  <div className={styles.directorsFieldContainer}>
                    {renderDirectorFields(namePrefix, directorIndex)}
                  </div>
                  {showDirectorFooterActions(index) &&
                    <div className={`${styles.directorFooter}`}>
                      <button type="button" className={styles.cancelButton} onClick={() => cancelIt(index, item)}> Cancel</button>
                      <button type="button" className={idSaveDirectorDisable(index) ? styles.disabled : ''} disabled={idSaveDirectorDisable(index)} onClick={() => onDirectorSave(index)}> Save</button>
                    </div>
                  }
                </Accordion>
                );
              })}
            </div>
            {(!isSoleTrader && values?.directors?.length <= 4) && (
              <div className={styles.addMoreDirectorDiv}>
                <div
                  className={`${styles.addMoreDirectorInnerDiv} ${isAddDirectorDisabled() ? styles.disabled : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    if (!isAddDirectorDisabled()) {
                      handleAddDirectorClick();
                    }
                  }}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && !isAddDirectorDisabled()) {
                      handleAddDirectorClick();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  style={{
                    opacity: isAddDirectorDisabled() ? 0.5 : 1,
                    cursor: isAddDirectorDisabled() ? 'not-allowed' : 'pointer'
                  }}
                >
                  <div className={styles.iconContainer}>
                    <span className={styles.plusIcon}>+</span>
                  </div>
                  <span className={styles.iconLabel}>Add another Director</span>
                </div>
              </div>
            )}

            {isOpenDirectorDelete && (
              <ConfirmModal
                closeCallback={cancelDirectorDeletion}
                icon={CloseIcon}
                title={'Are you sure?'}
                subTitle={'You are about to delete a director'}
                acceptLabel={'Delete director'}
                cancelLabel={'Cancel'}
                onAcceptClick={deleteDirector}
              />

            )}
            {isOpenSaveCurrentDirectorConfirmationPopup && (
              <ConfirmModal
                closeCallback={cancelAddNewDirector}
                icon={CloseIcon}
                title={'Are you sure?'}
                subTitle={'If you leave before saving, your changes will be lost.'}
                cancelLabel={'Cancel'}
              />

            )}
            {isOpenNextConfirmationPopup && (
              <ConfirmModal
                closeCallback={cancelNextClick}
                icon={CloseIcon}
                title={'Are you sure?'}
                subTitle={'If you leave before saving, your changes will be lost.'}
                acceptLabel={'Continue'}
                cancelLabel={'Cancel'}
                onAcceptClick={confirmNextClick}
              />
            )}
          </div>
          :
          <div className={styles.formWrapper}>
            {renderFormFields()}
          </div>
      }
      {shouldShowFileUpload && <BankStatementUpload setIsDocumentUploaded={setIsDocumentUploaded} />}
      {requireFooter && (
        <Footer
          leftTitle={getLeftButtonLabel()}
          rightTitle={getRightButtonLabel()}
          leftCallback={backClick}
          rightCallback={rightCallback()}
          shouldShowSubmit
          isNextAllowed={isNextAllowed}
        />
      )}
    </div>
  );
};

FormBuilder.propTypes = {
  fields: PropTypes.object,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  isMobile: PropTypes.bool,
  values: PropTypes.object,
  FormField: PropTypes.object,
  errors: PropTypes.object,
  submitState: PropTypes.object,
  backClick: PropTypes.func,
  submissionStates: PropTypes.object,
  nextClick: PropTypes.func,
  isDetailsCaptured: PropTypes.func,
  isBusinessVerified: PropTypes.bool,
  isSoleTrader: PropTypes.bool,
  personalAddress: PropTypes.object,
  touched: PropTypes.object,
  radioGroupOptions: PropTypes.array,
  handleRadioGroupChange: PropTypes.func,
  radioGroupDefaultValue: PropTypes.func,
  requireFooter: PropTypes.bool,
  handleChange: PropTypes.func,
  fetchedTotalDirectors: PropTypes.number,
  _journeyType: PropTypes.string,
  shouldShowSubmit: PropTypes.bool,
  isNextAllowed: PropTypes.bool,
  businessVerificationResult: PropTypes.object,
  customBlurHandlers: PropTypes.any,
  shouldShowFileUpload: PropTypes.bool,
  setIsDocumentUploaded: PropTypes.func,
  isVatOptional: PropTypes.any,
  sharedDirectorsValue: PropTypes.string,
  businessList: PropTypes.any,
  submitDirector: PropTypes.func,
  showConfirmButton: PropTypes.bool,
  showCancelButton: PropTypes.bool,
  sharedBusinessIdDirector: PropTypes.string,
  isVatNumberPreFetched: PropTypes.bool,
  leftButtonTitle: PropTypes.string,
  selectedBusiness: PropTypes.string,
  isNewBusiness: PropTypes.bool,
  pageName: PropTypes.string
};